import { ChangeEvent, FunctionComponent, useMemo, useState } from 'react';
import {
    Box,
    Button,
    IconButton,
    Stack,
    SvgIcon,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { useAuthentication } from '@/hooks/useAuthentication';
import { StarjackTable } from '@starjack/shared-components';
import { usePagination } from '@starjack/shared-components';
import PencilIcon from '@heroicons/react/24/solid/PencilIcon';
import Link from '@mui/material/Link';
import { useNavigate } from 'react-router-dom';
import {
    useDeleteConditionLinkMutation,
    useDeleteImportedConditionLinkMutation,
    useGetConditionLinksQuery,
    useGetImportedConditionLinksQuery,
} from '@/api/v2/conditionLinks';
import {
    ConditionLink,
    ImportedConditionLink,
    isOfTypeOriginalConditionLink,
} from '@/models/conditionLink';
import { TrashIcon } from '@heroicons/react/24/solid';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { useNotification } from '@starjack/features/notifications';
import { useGetLiftOperatorQuery } from '@/api/v2/liftOperator';
import debounce from 'lodash/debounce';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { usePostConditionLinkMutation } from '@/api/v2/conditionLinks';
import { ConfirmationDialog } from '@starjack/shared-components';

export const ConditionLinksContent: FunctionComponent = () => {
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { data: liftOperator } = useGetLiftOperatorQuery({ liftOperatorId });

    const {
        data: conditionLinks = [],
        isLoading,
        error: conditionLinksError,
    } = useGetConditionLinksQuery({
        liftOperatorId,
    });
    const {
        data: importedConditionLink = [],
        isLoading: importLoading,
        error: importedConditionLinksError,
    } = useGetImportedConditionLinksQuery({
        liftOperatorId,
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [conditionToDelete, setConditionToDelete] = useState<
        ConditionLink | ImportedConditionLink | null
    >(null);

    const allConditionLinks = useMemo(() => {
        return [...conditionLinks, ...importedConditionLink];
    }, [conditionLinks, importedConditionLink]);

    const filteredConditionLinks = useMemo(() => {
        if (!searchTerm) {
            return [];
        }

        const searchTermTerms = searchTerm.split(' ').filter(Boolean);

        if (!searchTermTerms.length) {
            return [];
        }

        return allConditionLinks.filter((conditionLink) =>
            searchTermTerms.every((term) =>
                conditionLink.name.toLowerCase().includes(term.toLowerCase())
            )
        );
    }, [allConditionLinks, searchTerm]);

    const onTextFieldChange = useMemo(
        () =>
            debounce((event: ChangeEvent<HTMLInputElement>) => {
                setSearchTerm(event.target.value);
            }, 100),
        [setSearchTerm]
    );

    const [deleteConditionLink, { isLoading: deleteConditionLinkLoading }] =
        useDeleteConditionLinkMutation();
    const [
        deleteImportedConditionLink,
        { isLoading: deleteImportedConditionLinkLoading },
    ] = useDeleteImportedConditionLinkMutation();
    const { currentItems, setNextPage, itemsPerPage, setItemsPerPage } =
        usePagination({
            items: allConditionLinks,
            itemsPerPage: 50,
        });

    const [postConditionLink, { isLoading: postConditionLinkLoading }] =
        usePostConditionLinkMutation();

    const { addNotification } = useNotification();
    const theme = useTheme();
    const navigate = useNavigate();

    const onCopy = (conditionLink: ConditionLink) => {
        const newConditionLink = {
            ...conditionLink,
            id: undefined,
            name: `${conditionLink.name} - Kopie`,
        };

        postConditionLink({ liftOperatorId, conditionLink: newConditionLink })
            .unwrap()
            .then(() => {
                addNotification({
                    message: 'Regelwerk erfolgreich kopiert',
                    type: 'success',
                });
            })
            .catch(() => {
                addNotification({
                    message:
                        'Fehler beim Kopieren. Bitte später noch einmal versuchen',
                    type: 'error',
                });
            });
    };

    const onDelete = (conditionLink: ConditionLink | ImportedConditionLink) => {
        const deleteAction = isOfTypeOriginalConditionLink(conditionLink)
            ? deleteConditionLink
            : deleteImportedConditionLink;

        deleteAction({ id: conditionLink.id, liftOperatorId })
            .unwrap()
            .then(() => {
                addNotification({
                    message: 'Regelwerk erfolgreich gelöscht',
                    type: 'success',
                });
                setConditionToDelete(null);
            })
            .catch((e) => {
                if (e?.data?.errors?.[0]?.error === 'condition_link_in_use') {
                    addNotification({
                        message:
                            'Regelwerk kann nicht gelöscht werden, da es noch in einer Produktkonfiguration verwendet wird',
                        type: 'error',
                    });
                } else {
                    addNotification({
                        message:
                            'Fehler beim Löschen. Bitte später noch einmal versuchen',
                        type: 'error',
                    });
                }
            });
    };

    const tableLoading =
        importLoading ||
        isLoading ||
        postConditionLinkLoading ||
        deleteConditionLinkLoading ||
        deleteImportedConditionLinkLoading;

    return (
        <>
            <Typography
                sx={{
                    mb: 8,
                }}
                variant='h4'
            >
                Regelwerke
            </Typography>

            <Stack spacing={theme.page.verticalSpacing}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        p: 3,
                    }}
                >
                    <TextField
                        onChange={onTextFieldChange}
                        label='Nach Regelwerk suchen'
                        sx={{
                            mr: 'auto',
                            width: '400px',
                        }}
                    />

                    <Button
                        sx={{
                            mr: 2,
                        }}
                        onClick={() => navigate('/regelwerke/importieren')}
                        variant='contained'
                    >
                        + Neues Regelwerk importieren
                    </Button>

                    <Button
                        onClick={() => navigate('/regelwerke/neu')}
                        variant='contained'
                        disabled={!liftOperator?.dynamicPricingEnabled}
                    >
                        + Neues Regelwerk anlegen
                    </Button>
                </Box>

                <StarjackTable<ConditionLink | ImportedConditionLink>
                    count={allConditionLinks?.length}
                    itemId={'id'}
                    items={
                        searchTerm.length
                            ? filteredConditionLinks
                            : currentItems
                    }
                    columns={[
                        {
                            label: 'Name',
                            itemKey: 'name',
                        },

                        {
                            label: 'Extern',
                            itemTransform: (item) =>
                                isOfTypeOriginalConditionLink(item)
                                    ? 'Nein'
                                    : 'Ja',
                        },

                        {
                            label: 'Verwendungen',
                            itemTransform: (item) => (
                                <Typography>{item.usages.length}</Typography>
                            ),
                            options: {
                                headerCellProps: {
                                    align: 'right',
                                },
                                rowCellProps: {
                                    align: 'right',
                                },
                            },
                        },

                        {
                            label: 'Vollständig konfiguriert',
                            itemTransform: (item) => {
                                if (isOfTypeOriginalConditionLink(item)) {
                                    return (
                                        <SvgIcon
                                            fontSize='medium'
                                            color='success'
                                        >
                                            <CheckCircleIcon />
                                        </SvgIcon>
                                    );
                                } else {
                                    return !item.missingPersonTypesMapping
                                        .length ? (
                                        <SvgIcon
                                            fontSize='medium'
                                            color='success'
                                        >
                                            <CheckCircleIcon />
                                        </SvgIcon>
                                    ) : (
                                        <SvgIcon
                                            fontSize='medium'
                                            color='error'
                                        >
                                            <ExclamationCircleIcon />
                                        </SvgIcon>
                                    );
                                }
                            },
                            options: {
                                headerCellProps: {
                                    align: 'right',
                                },
                                rowCellProps: {
                                    align: 'right',
                                },
                            },
                        },

                        {
                            label: 'Aktion',
                            itemTransform: (item) => (
                                <Stack
                                    justifyContent='flex-end'
                                    direction='row'
                                    spacing={2}
                                >
                                    <Link
                                        href={`/regelwerke/details/?${
                                            isOfTypeOriginalConditionLink(item)
                                                ? 'id'
                                                : 'importId'
                                        }=${item.id}`}
                                    >
                                        <IconButton aria-label='Bearbeiten'>
                                            <SvgIcon fontSize='medium'>
                                                <PencilIcon />
                                            </SvgIcon>
                                        </IconButton>
                                    </Link>

                                    <IconButton
                                        aria-label='Löschen'
                                        onClick={() => {
                                            setConditionToDelete(item);
                                        }}
                                    >
                                        <SvgIcon fontSize='medium'>
                                            <TrashIcon />
                                        </SvgIcon>
                                    </IconButton>

                                    {isOfTypeOriginalConditionLink(item) ? (
                                        <IconButton
                                            aria-label='Kopieren'
                                            onClick={() => {
                                                onCopy(item);
                                            }}
                                        >
                                            <SvgIcon fontSize='medium'>
                                                <ContentCopyIcon />
                                            </SvgIcon>
                                        </IconButton>
                                    ) : null}
                                </Stack>
                            ),
                            options: {
                                headerCellProps: {
                                    align: 'right',
                                },
                                rowCellProps: {
                                    align: 'right',
                                },
                            },
                        },
                    ]}
                    loading={tableLoading}
                    error={conditionLinksError ?? importedConditionLinksError}
                    onRowsPerPageChange={setItemsPerPage}
                    rowsPerPage={itemsPerPage}
                    onPageChange={setNextPage}
                />
            </Stack>

            <ConfirmationDialog
                open={!!conditionToDelete}
                body='Soll das Regelwerk wirklich gelöscht werden?'
                confirmButtonText='Regelwerk löschen'
                onClose={() => {
                    setConditionToDelete(null);
                }}
                onConfirm={() => {
                    onDelete(
                        conditionToDelete as
                            ConditionLink | ImportedConditionLink
                    );
                }}
                deleteLoading={
                    deleteConditionLinkLoading ||
                    deleteImportedConditionLinkLoading
                }
            />
        </>
    );
};
